import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  collection, 
  query, 
  getDocs,
  orderBy,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { userService } from './userService';

class CommitteeService {
  constructor() {
    this.collectionName = 'committees';
  }

  // Create a new committee
  async createCommittee(committeeData) {
    try {
      const committeeId = `committee_${Date.now()}`;
      const committeeRef = doc(db, this.collectionName, committeeId);
      
      const committee = {
        id: committeeId,
        name: committeeData.name.trim(),
        members: committeeData.members || [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await setDoc(committeeRef, committee);
      
      // Update users with committee membership
      if (committeeData.members && committeeData.members.length > 0) {
        await this.updateUserCommitteeMemberships(committeeData.members, committeeId, 'add');
      }
      
      console.log('Committee created successfully');
      return { ...committee, id: committeeId };
    } catch (error) {
      console.error('Error creating committee:', error);
      throw new Error('Failed to create committee');
    }
  }

  // Get all committees
  async getAllCommittees() {
    try {
      const committeesRef = collection(db, this.collectionName);
      const q = query(committeesRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const committees = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        committees.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || null,
          updatedAt: data.updatedAt?.toDate() || null
        });
      });
      
      return committees;
    } catch (error) {
      console.error('Error fetching committees:', error);
      throw new Error('Failed to fetch committees');
    }
  }

  // Get committee by ID
  async getCommitteeById(committeeId) {
    try {
      const committeeRef = doc(db, this.collectionName, committeeId);
      const committeeSnap = await getDoc(committeeRef);
      
      if (committeeSnap.exists()) {
        const data = committeeSnap.data();
        return {
          id: committeeSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate() || null,
          updatedAt: data.updatedAt?.toDate() || null
        };
      } else {
        throw new Error('Committee not found');
      }
    } catch (error) {
      console.error('Error fetching committee:', error);
      throw new Error('Failed to fetch committee');
    }
  }

  // Update committee
  async updateCommittee(committeeId, updateData) {
    try {
      const committeeRef = doc(db, this.collectionName, committeeId);
      
      // Get current committee data
      const currentCommittee = await this.getCommitteeById(committeeId);
      const oldMembers = currentCommittee.members || [];
      const newMembers = updateData.members || [];
      
      // Update committee document
      const updates = {
        ...updateData,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(committeeRef, updates);
      
      // Update user committee memberships
      const membersToAdd = newMembers.filter(id => !oldMembers.includes(id));
      const membersToRemove = oldMembers.filter(id => !newMembers.includes(id));
      
      if (membersToAdd.length > 0) {
        await this.updateUserCommitteeMemberships(membersToAdd, committeeId, 'add');
      }
      
      if (membersToRemove.length > 0) {
        await this.updateUserCommitteeMemberships(membersToRemove, committeeId, 'remove');
      }
      
      console.log('Committee updated successfully');
      return updates;
    } catch (error) {
      console.error('Error updating committee:', error);
      throw new Error('Failed to update committee');
    }
  }

  // Delete committee
  async deleteCommittee(committeeId) {
    try {
      // Get committee data before deletion
      const committee = await this.getCommitteeById(committeeId);
      
      // Remove committee from all users
      if (committee.members && committee.members.length > 0) {
        await this.updateUserCommitteeMemberships(committee.members, committeeId, 'remove');
      }
      
      // Delete committee document
      const committeeRef = doc(db, this.collectionName, committeeId);
      await deleteDoc(committeeRef);
      
      console.log('Committee deleted successfully');
    } catch (error) {
      console.error('Error deleting committee:', error);
      throw new Error('Failed to delete committee');
    }
  }

  // Update user committee memberships
  async updateUserCommitteeMemberships(userIds, committeeId, action) {
    try {
      const batch = writeBatch(db);
      
      for (const userId of userIds) {
        const userRef = doc(db, 'users', userId);
        const userData = await userService.getUserById(userId);
        
        let committees = userData.committees || [];
        
        if (action === 'add') {
          if (!committees.includes(committeeId)) {
            committees.push(committeeId);
          }
        } else if (action === 'remove') {
          committees = committees.filter(id => id !== committeeId);
        }
        
        batch.update(userRef, { 
          committees,
          updatedAt: serverTimestamp()
        });
      }
      
      await batch.commit();
      console.log('User committee memberships updated');
    } catch (error) {
      console.error('Error updating user committee memberships:', error);
      throw new Error('Failed to update user memberships');
    }
  }

  // Check if committee name exists
  async isCommitteeNameExists(name, excludeId = null) {
    try {
      const committees = await this.getAllCommittees();
      return committees.some(committee => 
        committee.name.toLowerCase() === name.toLowerCase().trim() && 
        committee.id !== excludeId
      );
    } catch (error) {
      console.error('Error checking committee name:', error);
      return false;
    }
  }

  // Get committees for a specific user
  async getUserCommittees(userId) {
    try {
      const committees = await this.getAllCommittees();
      return committees.filter(committee => 
        committee.members && committee.members.includes(userId)
      );
    } catch (error) {
      console.error('Error fetching user committees:', error);
      throw new Error('Failed to fetch user committees');
    }
  }

  // Validate committee data
  validateCommitteeData(data) {
    const errors = {};
    
    if (!data.name || !data.name.trim()) {
      errors.name = 'Committee name is required';
    } else if (data.name.trim().length < 3) {
      errors.name = 'Committee name must be at least 3 characters';
    } else if (data.name.trim().length > 100) {
      errors.name = 'Committee name cannot exceed 100 characters';
    }
    
    if (!data.members || data.members.length === 0) {
      errors.members = 'At least one member must be selected';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
}

export const committeeService = new CommitteeService();